import { FunctionComponent, useCallback } from "react";
import useTenant from "../src/useTenant";
import { tenantDisplayName } from "../src/useTenant";
import { useTransition } from "@vanyapr/transition-hook";
import { EnvData, PendingInvitation, Tenant } from "../src/types";
import FocusLock from "react-focus-lock";
import { HiOutlineQuestionMarkCircle } from "react-icons/hi";
import { HiXCircle } from "react-icons/hi";
import useOutsideClick from "../src/useOutsideClick";
import useEnv from "../src/useEnv";
import Schedule1x1Cta from "./Schedule1x1Cta";
import TenantKindBadge from "./TenantKindBadge";
import { useOptionalUser } from "@/src/useUser";
import Button from "./Button";
import { useRouter } from "next/router";
import { useApertureAutoSwitch } from "@/src/useApertureAutoSwitch";

const EnvironmentSwitcher: FunctionComponent<React.PropsWithChildren> = ({
  children,
}) => {
  const { user } = useOptionalUser();
  const { shouldShowSwitcher, tenants, tenant, setTenant } = useTenant();
  const { selectedEnv: environment, setSelectedEnvName } = useEnv();
  const { stage, shouldMount } = useTransition(shouldShowSwitcher, 300);
  const setEnvironment = useCallback(
    (env: EnvData) => {
      setSelectedEnvName(env.name);
    },
    [setSelectedEnvName]
  );

  const containerClass = stage === "enter" ? "opacity-1" : "opacity-0";
  const boxClass = stage === "enter" ? "" : "scale-95 translate-y-2";

  return (
    <>
      {children}
      {shouldMount && (
        <FocusLock>
          <div
            className={`transition-opacity fixed z-40 inset-0 flex-col flex items-center bg-gray-300 bg-opacity-80 ${containerClass}`}
          >
            <div className="grow"></div>
            <div
              className={`transition-transform w-full max-w-[540px] m-4 ${boxClass}`}
            >
              <SwitcherModal
                tenants={tenants}
                setTenant={setTenant}
                currentTenant={tenant}
                currentEnvironment={environment}
                setEnvironment={setEnvironment}
                pendingInvitations={user?.pending_invitations ?? []}
              />
            </div>
            {/* NB: these empty elements push the SwitcherModal up towards the
            top of the page. Removing them centers the content vertically in the window. */}
            <div className="grow"></div>
            <div className="grow"></div>
            <div className="grow"></div>
          </div>
        </FocusLock>
      )}
    </>
  );
};

const SwitcherModal: FunctionComponent<{
  tenants: Tenant[];
  setTenant: (tenant: Tenant) => void;
  currentTenant: Tenant | null;
  currentEnvironment: EnvData | null;
  setEnvironment: (env: EnvData) => void;
  pendingInvitations: PendingInvitation[];
}> = ({
  tenants,
  setTenant,
  setEnvironment,
  currentTenant,
  currentEnvironment,
  pendingInvitations,
}) => {
  const hasLiveTenant = !!tenants.find(
    (tenant) => tenant.kind === "production"
  );
  const router = useRouter();
  const { maybeAutoSwitchToAgents } = useApertureAutoSwitch();

  // On close, just set the environment/tenant equal to what it is now
  const close = useCallback(() => {
    const newTenant = currentTenant || tenants[0];
    setTenant(newTenant);
    setEnvironment(currentEnvironment || newTenant.environments[0]);
  }, [setTenant, tenants, currentTenant, currentEnvironment, setEnvironment]);

  const setEnvironmentAndTenant = (env: EnvData, tenant: Tenant) => {
    setTenant(tenant);
    setEnvironment(env);
    maybeAutoSwitchToAgents(env.name, env.pub_id);
  };
  const outsideClickRef = useOutsideClick<HTMLDivElement>(close);
  // Sort tenants by kind (putting production environments first)
  // This is a hack but its easier than partitioning by kind and concatenating.
  const sortedTenants = tenants
    .slice()
    .sort((a, b) => a.kind.localeCompare(b.kind));
  return (
    <div ref={outsideClickRef} className="p-6 bg-white rounded-lg shadow-lg">
      <div className="relative">
        <button
          type="button"
          title="Close"
          onClick={close}
          className="absolute -top-2 -right-2 text-2xl transition-colors hover:text-red-500"
        >
          <HiXCircle />
        </button>
      </div>

      <div className="text-xl font-space">Choose an environment:</div>
      <div className="overflow-y-auto max-h-[80vh]">
        {sortedTenants.map((tenant, i) => (
          <span key={tenant.id}>
            {i > 0 && <div className="my-6" />}
            <div className="text-sm flex items-center mt-3 mb-2 space-x-2">
              {tenant.kind === "sandbox" ? (
                <span className="text-gray-600">
                  {tenantDisplayName(tenant)}
                </span>
              ) : (
                <span>
                  <span className="text-gray-600">Organization: </span>
                  <span className="font-semibold">
                    {tenantDisplayName(tenant)}
                  </span>
                </span>
              )}
              <TenantKindBadge kind={tenant.kind} />
            </div>
            {tenant.environments.map((env) => (
              <button
                key={tenant.id + env.name}
                role="button"
                tabIndex={0}
                className={`py-2 px-3 bg-gray-100 rounded transition-colors
                         hover:bg-gray-50 hover:border-gray-300 border mb-2 last:mb-0
                           focus:outline-2 outline-blue-500 flex items-center
                           justify-between w-full ${
                             currentEnvironment?.pub_id === env.pub_id &&
                             "bg-yellow-50 "
                           }`}
                onClick={() => setEnvironmentAndTenant(env, tenant)}
              >
                <div className="text-left">
                  <div className="">
                    <span className="font-normal text-lg">{env.name} </span>
                    {currentEnvironment?.pub_id == env.pub_id && (
                      <span className="font-normal text-lg italic">
                        (active)
                      </span>
                    )}
                  </div>
                  {/* <div className="text-sm text-gray-600">{tenant.id}</div> */}
                </div>
                <span>&rarr;</span>
              </button>
            ))}
          </span>
        ))}
        {pendingInvitations.length > 0 && (
          <div className="py-4">
            <div>
              <div className="text-gray-600">
                You have been invited to the following organizations:
              </div>
              <div className="font-semibold pl-2">
                <ul>
                  {pendingInvitations.map((invitation) => (
                    <li key={`invitation-{invitation.tenant.name}`}>
                      {invitation.tenant.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Button
              small
              theme="primary"
              className="my-2"
              onClick={(e) => {
                e.preventDefault();
                close();
                router.push("/user-settings");
              }}
            >
              View invitations
            </Button>{" "}
          </div>
        )}
        <div className="border-b my-4" />
        <div className="">
          <div className="flex items-center space-x-1 mb-1">
            <HiOutlineQuestionMarkCircle className="inline" />{" "}
            <span className="font-space">What is this?</span>
          </div>
          <div className="text-sm">
            All developers who sign up get their own personal organization in{" "}
            <span className="text-[8pt] text-white rounded font-bold bg-gray-500 uppercase py-0.5 px-1">
              developer
            </span>{" "}
            mode, with non-production environments for experimenting locally.{" "}
            Environments in{" "}
            <span className="text-[8pt] text-white rounded font-bold bg-green-700 uppercase py-0.5 px-1">
              business
            </span>{" "}
            organizations use infrastructure that is deployed near your servers
            for minimal read latency.
            {!hasLiveTenant && (
              <div className="mt-2">
                To set up a production environment in a{" "}
                <span className="text-[8pt] text-white rounded font-bold bg-green-700 uppercase py-0.5 px-1">
                  business
                </span>{" "}
                organization,{" "}
                <Schedule1x1Cta
                  utmContent="tenant_switcher"
                  utmSource="app"
                  className="underline text-blue-800"
                >
                  set up a 1x1 with an Oso engineer
                </Schedule1x1Cta>
                .
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentSwitcher;
